import { memo } from 'react'
import { NodeProps } from 'reactflow'
import { BaseNode } from './BaseNode'
import {
  CpuChipIcon,
  WrenchIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon,
  UserIcon,
  PlayIcon,
  StopIcon,
  ExclamationTriangleIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'

// Start Node
export const StartNode = memo(({ selected }: NodeProps) => {
  return (
    <BaseNode
      title="Start"
      icon={<PlayIcon className="w-4 h-4" />}
      color="green"
      selected={selected}
      hasInput={false}
      hasOutput={true}
    >
      Workflow entry point
    </BaseNode>
  )
})
StartNode.displayName = 'StartNode'

// End Node
export const EndNode = memo(({ selected }: NodeProps) => {
  return (
    <BaseNode
      title="End"
      icon={<StopIcon className="w-4 h-4" />}
      color="red"
      selected={selected}
      hasInput={true}
      hasOutput={false}
    >
      Workflow completion
    </BaseNode>
  )
})
EndNode.displayName = 'EndNode'

// LLM Call Node
export const LLMNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="LLM Call"
      icon={<CpuChipIcon className="w-4 h-4" />}
      color="blue"
      selected={selected}
    >
      <div className="space-y-1">
        <div className="font-medium">{data.model || 'GPT-4'}</div>
        {data.prompt && (
          <div className="text-xs text-gray-500 truncate max-w-[180px]">
            {data.prompt}
          </div>
        )}
      </div>
    </BaseNode>
  )
})
LLMNode.displayName = 'LLMNode'

// Tool Call Node
export const ToolNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Tool Call"
      icon={<WrenchIcon className="w-4 h-4" />}
      color="purple"
      selected={selected}
    >
      <div className="space-y-1">
        <div className="font-medium">{data.toolName || 'Select Tool'}</div>
        {data.description && (
          <div className="text-xs text-gray-500 truncate max-w-[180px]">
            {data.description}
          </div>
        )}
      </div>
    </BaseNode>
  )
})
ToolNode.displayName = 'ToolNode'

// Conditional Node
export const ConditionalNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Conditional"
      icon={<QuestionMarkCircleIcon className="w-4 h-4" />}
      color="orange"
      selected={selected}
    >
      <div className="space-y-1">
        {data.condition ? (
          <div className="font-mono text-xs bg-white p-1 rounded">
            {data.condition}
          </div>
        ) : (
          <div className="text-gray-500">Define condition</div>
        )}
      </div>
    </BaseNode>
  )
})
ConditionalNode.displayName = 'ConditionalNode'

// Loop Node
export const LoopNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Loop"
      icon={<ArrowPathIcon className="w-4 h-4" />}
      color="orange"
      selected={selected}
    >
      <div className="space-y-1">
        <div className="font-medium">
          {data.loopType === 'foreach' ? 'For Each' : 'While'}
        </div>
        {data.iterations && (
          <div className="text-xs text-gray-500">Max: {data.iterations}</div>
        )}
      </div>
    </BaseNode>
  )
})
LoopNode.displayName = 'LoopNode'

// Human Input Node
export const HumanInputNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Human Input"
      icon={<UserIcon className="w-4 h-4" />}
      color="gray"
      selected={selected}
    >
      <div className="space-y-1">
        {data.prompt ? (
          <div className="text-xs truncate max-w-[180px]">{data.prompt}</div>
        ) : (
          <div className="text-gray-500">Request user input</div>
        )}
      </div>
    </BaseNode>
  )
})
HumanInputNode.displayName = 'HumanInputNode'

// Agent Node - Execute an existing agent
export const AgentNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Agent"
      icon={<CpuChipIcon className="w-4 h-4" />}
      color="blue"
      selected={selected}
    >
      <div className="space-y-1">
        <div className="font-medium">{data.agentName || 'Select Agent'}</div>
        {data.agentId && (
          <div className="text-xs text-gray-500 font-mono truncate">
            {data.agentId.slice(0, 8)}
          </div>
        )}
      </div>
    </BaseNode>
  )
})
AgentNode.displayName = 'AgentNode'

// Try/Catch Node - Error handling
export const TryCatchNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Try/Catch"
      icon={<ExclamationTriangleIcon className="w-4 h-4" />}
      color="red"
      selected={selected}
    >
      <div className="space-y-1">
        <div className="font-medium">
          {data.strategy === 'catchAndFallback'
            ? 'With Fallback'
            : data.strategy === 'catchAndTransform'
            ? 'Transform Error'
            : 'Catch & Continue'}
        </div>
        {data.errorTypes && data.errorTypes.length > 0 && (
          <div className="text-xs text-gray-500">
            {data.errorTypes.slice(0, 2).join(', ')}
            {data.errorTypes.length > 2 && ` +${data.errorTypes.length - 2}`}
          </div>
        )}
      </div>
    </BaseNode>
  )
})
TryCatchNode.displayName = 'TryCatchNode'

// Retry Node - Retry with backoff
export const RetryNode = memo(({ data, selected }: NodeProps) => {
  return (
    <BaseNode
      title="Retry"
      icon={<ArrowUturnLeftIcon className="w-4 h-4" />}
      color="orange"
      selected={selected}
    >
      <div className="space-y-1">
        <div className="font-medium">
          {data.maxAttempts || 3} attempts
        </div>
        <div className="text-xs text-gray-500">
          {data.retryStrategy === 'exponential'
            ? 'Exponential'
            : data.retryStrategy === 'linear'
            ? 'Linear'
            : 'Fixed'}{' '}
          ({data.baseDelay || 1000}ms)
        </div>
      </div>
    </BaseNode>
  )
})
RetryNode.displayName = 'RetryNode'

// Export node types mapping
export const nodeTypes = {
  start: StartNode,
  end: EndNode,
  llm: LLMNode,
  tool: ToolNode,
  conditional: ConditionalNode,
  loop: LoopNode,
  humanInput: HumanInputNode,
  agent: AgentNode,
  tryCatch: TryCatchNode,
  retry: RetryNode,
}
